import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { Sms, Email, ContentCopy, Close } from '@mui/icons-material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useTranslation } from 'react-i18next';

export type ShareChannel = 'sms' | 'whatsapp' | 'email' | 'copy';

interface ShareBottomSheetProps {
  open: boolean;
  onClose: () => void;
  onSelectChannel: (channel: ShareChannel) => void;
}

const channels: { key: ShareChannel; icon: React.ReactNode; labelKey: string }[] = [
  { key: 'sms', icon: <Sms color="primary" />, labelKey: 'offerDetail.shareChannels.sms' },
  {
    key: 'whatsapp',
    icon: <WhatsAppIcon sx={{ color: '#25D366' }} />,
    labelKey: 'offerDetail.shareChannels.whatsapp',
  },
  { key: 'email', icon: <Email color="primary" />, labelKey: 'offerDetail.shareChannels.email' },
  {
    key: 'copy',
    icon: <ContentCopy color="primary" />,
    labelKey: 'offerDetail.shareChannels.copy',
  },
];

export default function ShareBottomSheet({
  open,
  onClose,
  onSelectChannel,
}: Readonly<ShareBottomSheetProps>) {
  const { t } = useTranslation();

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            pb: 2,
          },
        },
      }}
    >
      <Box sx={{ pt: 1, pb: 1 }}>
        <Box
          sx={{
            width: 40,
            height: 4,
            bgcolor: 'grey.300',
            borderRadius: 2,
            mx: 'auto',
            mb: 1,
          }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
            {t('offerDetail.shareChannels.title')}
          </Typography>
          <Box
            component="button"
            onClick={onClose}
            sx={{
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              p: 0.5,
              display: 'flex',
              color: 'text.secondary',
            }}
            aria-label={t('common.close')}
          >
            <Close fontSize="small" />
          </Box>
        </Box>
        <List>
          {channels.map(({ key, icon, labelKey }) => (
            <ListItem key={key} disablePadding>
              <ListItemButton
                onClick={() => {
                  onSelectChannel(key);
                  onClose();
                }}
              >
                <ListItemIcon>{icon}</ListItemIcon>
                <ListItemText
                  primary={t(labelKey)}
                  slotProps={{
                    primary: { sx: { fontSize: '16px', fontWeight: 500 } },
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
    </Drawer>
  );
}
